import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../auth/AuthProvider';
import { managedLocations } from '../../auth/managedLocations';
import { SuccessDataService, ClientSuccessMetric, ClientData } from '../services/successDataService';
import { buildSuccessOverview } from '../utils/successOverviewModel';

interface UseSuccessDataResult {
  isLoading: boolean;
  error: string | null;
  successData: ClientSuccessMetric[];
  recentUpdates: ClientSuccessMetric[];
  overdueUpdates: ClientSuccessMetric[];
  clientSuccessMap: Map<string, ClientSuccessMetric[]>;
  refresh: () => Promise<void>;
  statistics: {
    goalsAchievedLast30Days: number;
    targetsAchievedLast30Days: number;
    clientsWithAtLeastOneGoal: number;
    totalClientsWithData: number;
  };
}

export const useSuccessData = (): UseSuccessDataResult => {
  const { user, userMetadata } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<ClientSuccessMetric[]>([]);
  const [recentUpdates, setRecentUpdates] = useState<ClientSuccessMetric[]>([]);
  const [overdueUpdates, setOverdueUpdates] = useState<ClientSuccessMetric[]>([]);
  const [clientSuccessMap, setClientSuccessMap] = useState<Map<string, ClientSuccessMetric[]>>(new Map());
  const [statistics, setStatistics] = useState({
    goalsAchievedLast30Days: 0,
    targetsAchievedLast30Days: 0,
    clientsWithAtLeastOneGoal: 0,
    totalClientsWithData: 0
  });

  const fetchData = async () => {
    if (!user || !userMetadata) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await SuccessDataService.fetchClientSuccessData(
        userMetadata.role,
        managedLocations(userMetadata),
        userMetadata.coach_id
      );

      const { successMetrics: data, clients } = result;

      const overview = buildSuccessOverview(data, clients, new Date());

      setStatistics(overview.statistics);

      setSuccessData(data);
      setRecentUpdates(overview.recentUpdates);
      setOverdueUpdates(overview.overdueUpdates);
      setClientSuccessMap(overview.clientSuccessMap);
    } catch (err) {
      console.error('Error fetching success data:', err);
      setError('Failed to load client success data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    // Clear cache before refreshing
    if (userMetadata) {
      SuccessDataService.clearCache(userMetadata.role, managedLocations(userMetadata));
    }
    await fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [user, userMetadata]);

  return {
    isLoading,
    error,
    successData,
    recentUpdates,
    overdueUpdates,
    clientSuccessMap,
    refresh,
    statistics
  };
};
